/**
 * Pod Exec 디버그 세션 계약 (ADR 0022 · Issue #10)
 * --------------------------------------------------------------------------
 * kubectl exec를 화면으로 옮긴 것이 아니라, **기본 꺼짐 · 시간 제한 · 대상 고정**의
 * 좁은 진단 창입니다. Grafana 대체도, 범용 터미널도 아닙니다.
 *
 * 규칙
 * - 브라우저는 명령을 보내지 않습니다. 서버가 소유한 **닫힌 shell profile**을 고를 뿐입니다.
 *   (shell 안에서 무엇을 입력하는지는 Kubernetes exec의 성질상 임의이며, UI가 그 사실을 고지합니다.)
 * - Pod 신원은 이름이 아니라 UID입니다. 교체된 Pod는 409 `exec_pod_replaced`입니다.
 * - WebSocket URL·query·로그·audit에 credential을 싣지 않습니다. 승급은 Path 제한
 *   HttpOnly SameSite=Strict 단발성 쿠키 하나로만 일어납니다.
 * - 입력(stdin)은 **어디에도 저장하지 않습니다.** audit에는 metadata만 남습니다. 출력은 기록이
 *   켜진 배포(ADR 0031)에서만 서버가 정제·마스킹한 뒤 보존 기간 동안 보관하고, 소유자와
 *   platform.admin만 다시 봅니다.
 */

import type { MaskedSpan } from "./index";

/** 서버가 소유한 shell profile 식별자. 브라우저는 이 중 하나만 고릅니다. */
export type ExecShellProfileId = "sh" | "bash" | "powershell";

/** 한 profile의 표시 정보. 실제 argv는 서버에만 있고 내려오지 않습니다. */
export interface ExecShellProfile {
  id: ExecShellProfileId;
  label: string;
  /** 어떤 컨테이너 이미지에서 성공할 가능성이 높은지에 대한 짧은 설명. */
  description: string;
}

/** exec 대상이 될 수 있는 container. init·ephemeral container는 포함하지 않습니다. */
export interface ExecContainerOption {
  name: string;
  /** informer 스냅숏이 본 실행 상태. Running이 아니면 선택할 수 없습니다. */
  state: "Running" | "Waiting" | "Terminated" | "Unknown";
  /** 선택 가능 여부. false면 UI가 비활성으로 두고 사유를 함께 보여줍니다. */
  selectable: boolean;
}

/**
 * 서버가 강제하는 상한. UI는 고르지 않고 표시만 합니다.
 *
 * 활동과 무관한 벽시계 상한은 없습니다(ADR 0036) — 그래서 "절대 상한" 항목도 없습니다.
 * 세션이 끝나는 시각은 인증이 있는 배포에서만 정해지고, ticket의 `sessionExpiresAt`에 실려 옵니다.
 */
export interface ExecSessionLimits {
  /** 양방향 활동이 없을 때 세션이 끝나는 시간(초). */
  idleSeconds: number;
  /** 유휴 종료 몇 초 전에 화면이 경고를 띄우는지. */
  idleWarningSeconds: number;
  /** 세션 하나가 보낼 수 있는 stdin 총 바이트. */
  maxInputBytes: number;
  /** 세션 하나가 받을 수 있는 출력 총 바이트. */
  maxOutputBytes: number;
  /** WebSocket 프레임 하나의 바이트 상한(양방향). */
  maxFrameBytes: number;
  /** 초당 허용하는 클라이언트 프레임 수. */
  maxFramesPerSecond: number;
  maxSessionsPerSubject: number;
  maxSessionsPerCluster: number;
  /** 발급된 세션 자격이 유효한 시간(초). 지나면 attach가 거절됩니다. */
  capabilitySeconds: number;
}

/**
 * `GET /api/v1/clusters/{clusterId}/pods/{name}/debug-session`
 *
 * informer 스냅숏에서만 만듭니다 — 이 경로는 Kubernetes API를 호출하지 않습니다. (ADR 0004)
 * 권한과 대상 확인을 **먼저** 통과한 요청만 여기까지 옵니다. 그래서 `enabled: false`가
 * 대상의 존재 여부를 새로 알려주지 않습니다.
 */
export interface ExecSessionCapability {
  clusterId: string;
  namespace: string;
  podName: string;
  podUid: string;
  /** 이 배포에서 Debug Terminal이 켜져 있는지. */
  enabled: boolean;
  /** 대상 클러스터가 이 기능을 지원하는지(informer·exec 경로 준비 여부). */
  supported: boolean;
  /** enabled·supported가 false일 때 UI가 그대로 보여줄 짧은 사유. */
  reason?: string;
  /**
   * 위험 고지 문구. **서버가 소유합니다.**
   *
   * exec는 조회가 아닙니다 — shell 안에서는 Secret 열람과 데이터 변경이 가능합니다.
   * 그 사실을 브라우저 번들이 따로 적어 두면 서버 정책이 바뀔 때 두 곳이 어긋나고,
   * 사용자는 서버가 강제하지 않는 약속을 읽게 됩니다.
   */
  warning: string;
  containers: ExecContainerOption[];
  profiles: ExecShellProfile[];
  limits: ExecSessionLimits;
  /** 세션 기록 정책(ADR 0031). 표시만 합니다 — 문구는 `warning`이 이미 담습니다. */
  transcript: ExecTranscriptPolicy;
  /**
   * 호출자 자신의 세션 요약(ADR 0030 결정 5·7). 레지스트리가 꺼졌거나 저장소에 닿지 못하면
   * 없습니다 — 그때 화면은 요약 없이 예전처럼 동작합니다.
   */
  mySessions?: ExecMySessions;
  generatedAt: string;
}

/**
 * Pod 상세 Debug Terminal 카드의 "내 세션"(ADR 0030 결정 7).
 * 범위는 호출자 자신뿐입니다 — admin이라도 남의 세션은 여기 없습니다.
 */
export interface ExecMySessions {
  /** 내 살아 있는 세션 하나 — 이 Pod의 것이 먼저이고, 없으면 다른 Pod에서 가장 최근에 시작한 것입니다. */
  live?: ExecSessionRecord;
  /** 이 Pod UID에서 가장 최근에 끝난 내 세션. 사유·시각과 "같은 대상으로 다시 열기"의 재료입니다. */
  lastHere?: ExecSessionRecord;
}

/** 세션 기록 정책(ADR 0031). */
export interface ExecTranscriptPolicy {
  enabled: boolean;
  /** 종료 후 보존 일수. enabled가 false면 0입니다. */
  retentionDays: number;
}

/** `POST .../debug-session` 본문. 임의 command·query는 받지 않습니다. */
export interface ExecSessionRequest {
  container: string;
  profile: ExecShellProfileId;
  tty: boolean;
}

/**
 * `POST .../debug-session` 응답.
 *
 * **credential이 없습니다.** 실제 승급은 같은 응답의 `Set-Cookie` 하나이고,
 * 그 쿠키는 `attachPath`에만 붙으며 한 번 쓰면 사라집니다.
 */
export interface ExecSessionTicket {
  sessionId: string;
  /** WebSocket을 열 경로. query string이 없습니다. */
  attachPath: string;
  /** 브라우저가 반드시 요청해야 하는 subprotocol. */
  subprotocol: string;
  container: string;
  profile: ExecShellProfileId;
  tty: boolean;
  podUid: string;
  /** 이 자격이 만료되는 시각(RFC3339). 지나면 attach가 401입니다. */
  expiresAt: string;
  /**
   * **이 세션이 실제로 끝나는 시각**(RFC3339, ADR 0032·0036).
   *
   * 인증이 있는 배포에서는 자격의 인증 만료이고, 없는 배포에서는 **빈 문자열**입니다 —
   * 거기에는 시각으로 정해진 종료가 없습니다. 화면은 값이 있을 때만 남은 시간을 셉니다.
   */
  sessionExpiresAt: string;
  limits: ExecSessionLimits;
}

/** 브라우저가 반드시 요청해야 하는 WebSocket subprotocol. 없으면 upgrade가 거절됩니다. */
export const EXEC_SESSION_SUBPROTOCOL = "k8s-dashboard.exec.v1";

/**
 * 채널 프리픽스. 모든 프레임은 **binary**이고 첫 바이트가 채널입니다.
 * Kubernetes의 채널 다중화와 같은 모양이라 BFF가 새 프로토콜을 발명하지 않습니다.
 */
export const EXEC_CHANNEL = {
  /** 브라우저 → BFF: 터미널 입력. */
  stdin: 0,
  /** BFF → 브라우저: 표준 출력. */
  stdout: 1,
  /** BFF → 브라우저: 표준 오류. */
  stderr: 2,
  /** BFF → 브라우저: 종료 상태(JSON `ExecSessionStatus`). 마지막 프레임입니다. */
  status: 3,
  /** 브라우저 → BFF: 터미널 크기(JSON `ExecResizeFrame`). */
  resize: 4,
} as const;

/** 터미널 크기 변경. 값은 서버가 유계로 검증하고, 범위를 벗어나면 세션이 끝납니다. */
export interface ExecResizeFrame {
  width: number;
  height: number;
}

/** 세션이 끝난 이유. 닫힌 집합이며 metric 라벨과 같은 값입니다. */
export type ExecSessionCloseReason =
  | "completed"
  | "idle_timeout"
  | "absolute_timeout"
  | "input_bytes_exceeded"
  | "output_bytes_exceeded"
  | "frame_too_large"
  | "frame_rate_exceeded"
  | "protocol_error"
  | "backpressure"
  | "client_closed"
  | "upstream_error"
  | "pod_replaced"
  | "server_shutdown"
  | "feature_disabled"
  /** 공유 저장소에서 동시성 예약(lease)을 갱신하지 못했습니다. fail closed입니다. */
  | "lease_lost"
  /** 관리자·본인이 목록에서 강제 종료했습니다. (ADR 0030) */
  | "terminated_by_operator"
  /** 발급 시 기록한 인증 만료(Bearer exp·세션 만료)에 도달했습니다. (ADR 0030) */
  | "auth_expired";

/**
 * 마지막 프레임(채널 3)입니다.
 *
 * **본문을 담지 않습니다.** 종료 코드와 닫힌 사유 코드, 그리고 서버가 소유한
 * 짧은 문구만 있습니다. 원격 명령의 stdout/stderr 조각은 실리지 않습니다.
 */
export interface ExecSessionStatus {
  reason: ExecSessionCloseReason;
  /** 사용자에게 보여줄 짧은 문구. 내부 주소·질의·스택트레이스를 담지 않습니다. */
  message: string;
  /** 원격 프로세스 종료 코드. 알 수 없으면 없습니다. */
  exitCode?: number;
}

/**
 * Debug Sessions 관리 화면의 세션 한 줄. (ADR 0030)
 *
 * **터미널 본문은 없습니다.** 무엇을 열었는가만 담고 무엇을 쳤는가는 담지 않습니다.
 */
export interface ExecSessionRecord {
  sessionId: string;
  subject: string;
  clusterId: string;
  namespace: string;
  podName: string;
  podUid: string;
  container: string;
  profile: ExecShellProfileId;
  startedAt: string;
  lastActivityAt: string;
  /** 아직 열려 있으면 true. 아니면 이력이고 endedAt·reason이 채워집니다. */
  live: boolean;
  endedAt?: string;
  reason?: ExecSessionCloseReason;
  /** 종료 사유의 사용자 문구. 서버가 소유합니다 — 화면이 사유 코드를 따로 번역하지 않습니다. */
  reasonMessage?: string;
  terminatedBy?: string;
  inputBytes: number;
  outputBytes: number;
  /** 세션을 처리하는(했던) API 인스턴스 표기. 진단용입니다. */
  replica: string;
}

/** GET /api/v1/exec-sessions 응답. 화면 하나 = 요청 하나. */
export interface ExecSessionListResponse {
  sessions: ExecSessionRecord[];
  /** true면 접근 가능한 클러스터 전체의 세션. 아니면 자기 세션만. */
  isAdmin: boolean;
  /** true면 이력이 상한(100)에서 잘렸습니다. */
  truncated: boolean;
  /** true면 이 배포가 세션 기록을 보관합니다(ADR 0031). 화면이 "기록 보기"를 그릴지 정합니다. */
  transcriptsEnabled: boolean;
  generatedAt: string;
}

/** 기록의 한 줄. 마스킹된 본문과 가려진 구간뿐입니다 — `LogLine.masked`와 같은 모양입니다. */
export interface ExecTranscriptLine {
  text: string;
  masked: MaskedSpan[];
}

/**
 * `GET /api/v1/exec-sessions/{id}/transcript` 응답(ADR 0031).
 *
 * **stdin은 없습니다.** 정제·마스킹된 stdout·stderr 줄만 있고, 페이지는 chunk cursor입니다.
 * 원문 조회·복사 경로는 없습니다 — 있는 것이 마스킹된 본문 전부입니다.
 */
export interface ExecTranscriptResponse {
  sessionId: string;
  subject: string;
  clusterId: string;
  namespace: string;
  podName: string;
  podUid: string;
  container: string;
  profile: ExecShellProfileId;
  startedAt: string;
  endedAt?: string;
  /** 기록이 지워지는 시각. 살아 있는 세션은 시작 시각 기준 임시값입니다. */
  expiresAt: string;
  retentionDays: number;
  /** true면 세션이 아직 열려 있고 기록이 계속 늘어납니다. */
  live: boolean;
  /** true면 상한을 넘긴 뒷부분을 기록하지 않았습니다. */
  truncated: boolean;
  /** true면 저장 중 오류가 있어 일부가 빠졌을 수 있습니다. */
  failed: boolean;
  totalBytes: number;
  lines: ExecTranscriptLine[];
  /** 있으면 다음 페이지가 있습니다. 불투명한 값이며 그대로 `cursor`로 되돌려 보냅니다. */
  nextCursor?: string;
  generatedAt: string;
}

/** exec 경로가 돌려주는 `APIError.code`의 닫힌 집합입니다. */
export type ExecSessionErrorCode =
  | "exec_disabled"
  | "exec_unsupported"
  | "exec_forbidden"
  | "exec_pod_not_found"
  | "exec_pod_replaced"
  | "exec_pod_uid_required"
  | "exec_container_not_found"
  | "exec_container_not_running"
  | "exec_invalid_profile"
  | "exec_invalid_request"
  | "exec_quota"
  | "exec_capability_invalid"
  | "exec_origin_rejected"
  | "exec_protocol_unsupported"
  | "exec_upstream_error";

/**
 * 사용자 문구는 **서버가 소유합니다.** `APIError.message`와 `ExecSessionStatus.message`에
 * 이미 짧은 한국어 문구가 실려 옵니다. 코드는 분기용이고, 표시할 문장은 응답에서 그대로 씁니다.
 */

/**
 * `POST /api/v1/exec-sessions/{id}/keepalive` 응답 (ADR 0032).
 *
 * 갱신된 **세션 종료 시각**입니다. 돌려주지 않으면 화면은 발급 시점 값으로 계속 세어,
 * 서버가 미뤄 준 종료를 여전히 카운트다운합니다. 상한이 없는 배포에서는 빈 문자열입니다(ADR 0036).
 */
export interface ExecSessionKeepaliveResponse {
  sessionId: string;
  sessionExpiresAt: string;
  generatedAt: string;
}
